import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle, ShieldAlert } from 'lucide-react';

/**
 * Hook to manage compliance alert settings and trigger notifications
 * Based on user preferences from checkin_alert_settings table
 */
export const useComplianceAlerts = (userId, projectId = null) => {
  const [settings, setSettings] = useState(null);
  const [expiryThresholds, setExpiryThresholds] = useState([]);
  const [loading, setLoading] = useState(true);
  const prevGapsRef = useRef(null); // Store previous gap analysis to compare changes

  // Load user's alert settings
  useEffect(() => {
    if (userId) {
      loadAlertSettings();
    }
  }, [userId, projectId]);

  const loadAlertSettings = async () => {
    try {
      setLoading(true);

      // Try to get project-specific settings first, fall back to global
      let query = supabase
        .from('checkin_alert_settings')
        .select('*')
        .eq('user_id', userId);

      if (projectId) {
        query = query.eq('project_id', projectId);
      } else {
        query = query.is('project_id', null);
      }

      const { data, error } = await query.maybeSingle();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      // Use defaults if no settings found
      const defaultSettings = {
        alert_on_gap_created: true,
        alert_on_gap_resolved: true,
        alert_on_critical_shortage: true,
        alert_on_checkout_creates_gap: true,
        alert_on_cert_expiring_onsite: false,
        show_browser_notifications: true,
        show_toast_notifications: true,
        play_alert_sound: false,
        critical_shortage_threshold: 2,
        expiry_warning_days: 7
      };

      setSettings(data || defaultSettings);

      // Load expiry warning thresholds
      let thresholdsQuery = supabase
        .from('expiry_warning_thresholds')
        .select('*')
        .eq('user_id', userId);

      if (projectId) {
        thresholdsQuery = thresholdsQuery.eq('project_id', projectId);
      } else {
        thresholdsQuery = thresholdsQuery.is('project_id', null);
      }

      const { data: thresholds, error: thresholdsError } = await thresholdsQuery.order('days_before_expiry', { ascending: true });

      if (thresholdsError && thresholdsError.code !== 'PGRST116') {
        console.error('Error loading expiry thresholds:', thresholdsError);
      } else {
        setExpiryThresholds(thresholds || []);
      }
    } catch (error) {
      console.error('Error loading alert settings:', error);
      // Use defaults on error
      setSettings({
        alert_on_gap_created: true,
        alert_on_gap_resolved: true,
        alert_on_critical_shortage: true,
        alert_on_checkout_creates_gap: true,
        alert_on_cert_expiring_onsite: false,
        show_browser_notifications: true,
        show_toast_notifications: true,
        play_alert_sound: false,
        critical_shortage_threshold: 2,
        expiry_warning_days: 7
      });
      setExpiryThresholds([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Request browser notification permission if enabled
   */
  const requestNotificationPermission = useCallback(async () => {
    if (!settings?.show_browser_notifications) return false;

    if ('Notification' in window && Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }

    return Notification.permission === 'granted';
  }, [settings]);

  /**
   * Show browser notification
   */
  const showBrowserNotification = useCallback((title, body, icon = null) => {
    if (!settings?.show_browser_notifications) return;
    if (!('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;

    try {
      new Notification(title, {
        body,
        icon: icon || '/favicon.ico',
        badge: '/favicon.ico',
        tag: 'compliance-alert'
      });
    } catch (error) {
      console.error('Error showing browser notification:', error);
    }
  }, [settings]);

  /**
   * Play alert sound
   */
  const playAlertSound = useCallback(() => {
    if (!settings?.play_alert_sound) return;

    try {
      // Create a simple beep using Web Audio API
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 800;
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (error) {
      console.error('Error playing alert sound:', error);
    }
  }, [settings]);

  /**
   * Analyze compliance gaps and detect changes
   */
  const analyzeGapChanges = useCallback((currentGaps) => {
    if (!settings) return;

    // First time analyzing, just store current state
    if (!prevGapsRef.current) {
      prevGapsRef.current = currentGaps;
      return;
    }

    const prevGaps = prevGapsRef.current;
    const alerts = [];

    // Compare each requirement
    currentGaps.forEach(current => {
      const prev = prevGaps.find(p => p.id === current.id);

      if (!prev) return;

      // Gap created (was met, now shortage)
      if (prev.status === 'met' && current.status === 'shortage') {
        if (settings.alert_on_gap_created) {
          alerts.push({
            type: 'gap_created',
            requirement: current.name,
            gap: current.gap,
            severity: current.gap >= settings.critical_shortage_threshold ? 'critical' : 'warning'
          });
        }
      }

      // Gap resolved (was shortage, now met)
      if (prev.status === 'shortage' && current.status === 'met') {
        if (settings.alert_on_gap_resolved) {
          alerts.push({
            type: 'gap_resolved',
            requirement: current.name,
            severity: 'success'
          });
        }
      }

      // Gap worsened
      if (prev.status === 'shortage' && current.status === 'shortage' && current.gap > prev.gap) {
        if (settings.alert_on_gap_created) {
          alerts.push({
            type: 'gap_worsened',
            requirement: current.name,
            gap: current.gap,
            prevGap: prev.gap,
            severity: current.gap >= settings.critical_shortage_threshold ? 'critical' : 'warning'
          });
        }
      }

      // Gap improved
      if (prev.status === 'shortage' && current.status === 'shortage' && current.gap < prev.gap) {
        if (settings.alert_on_gap_resolved) {
          alerts.push({
            type: 'gap_improved',
            requirement: current.name,
            gap: current.gap,
            prevGap: prev.gap,
            severity: 'info'
          });
        }
      }
    });

    // Check for critical shortage (multiple requirements unmet)
    const shortageCount = currentGaps.filter(g => g.status === 'shortage').length;
    const prevShortageCount = prevGaps.filter(g => g.status === 'shortage').length;

    if (settings.alert_on_critical_shortage &&
        shortageCount >= settings.critical_shortage_threshold &&
        shortageCount > prevShortageCount) {
      alerts.push({
        type: 'critical_shortage',
        count: shortageCount,
        severity: 'critical'
      });
    }

    // Store current state for next comparison
    prevGapsRef.current = currentGaps;

    // Trigger notifications for all alerts
    alerts.forEach(alert => triggerAlert(alert));
  }, [settings]);

  /**
   * Trigger alert notifications based on alert type and user settings
   */
  const triggerAlert = useCallback((alert) => {
    if (!settings) return;

    let title = '';
    let message = '';
    let toastVariant = 'default';
    let icon = null;

    switch (alert.type) {
      case 'gap_created':
        title = 'Compliance Gap Created';
        message = `${alert.requirement} is now ${alert.gap} personnel short`;
        toastVariant = alert.severity === 'critical' ? 'destructive' : 'warning';
        icon = AlertTriangle;
        if (alert.severity === 'critical') {
          playAlertSound();
        }
        break;

      case 'gap_resolved':
        title = 'Compliance Gap Resolved';
        message = `${alert.requirement} requirement is now met`;
        toastVariant = 'success';
        icon = CheckCircle;
        break;

      case 'gap_worsened':
        title = 'Compliance Gap Worsened';
        message = `${alert.requirement} shortage increased from ${alert.prevGap} to ${alert.gap}`;
        toastVariant = alert.severity === 'critical' ? 'destructive' : 'warning';
        icon = ShieldAlert;
        if (alert.severity === 'critical') {
          playAlertSound();
        }
        break;

      case 'gap_improved':
        title = 'Compliance Gap Improved';
        message = `${alert.requirement} shortage reduced from ${alert.prevGap} to ${alert.gap}`;
        toastVariant = 'default';
        icon = CheckCircle;
        break;

      case 'critical_shortage':
        title = 'Critical Compliance Shortage';
        message = `${alert.count} requirements are currently unmet`;
        toastVariant = 'destructive';
        icon = AlertTriangle;
        playAlertSound();
        break;

      default:
        return;
    }

    // Show toast notification
    if (settings.show_toast_notifications) {
      const toastFn = toastVariant === 'destructive' ? toast.error :
                      toastVariant === 'warning' ? toast.warning :
                      toastVariant === 'success' ? toast.success :
                      toast.info;

      toastFn(message, {
        description: title,
        duration: alert.severity === 'critical' ? 10000 : 5000
      });
    }

    // Show browser notification
    if (settings.show_browser_notifications) {
      showBrowserNotification(title, message);
    }
  }, [settings, playAlertSound, showBrowserNotification]);

  /**
   * Check if an employee checking out would create a gap
   */
  const checkCheckoutImpact = useCallback((currentGaps, employeeUserId, teamMembers) => {
    if (!settings?.alert_on_checkout_creates_gap) return;

    // Find requirements this employee is helping to meet
    const affectedRequirements = currentGaps.filter(gap => {
      if (gap.status !== 'met') return false;

      // This is a simplified check - in reality you'd need to verify
      // if this specific employee is one of the valid personnel for this requirement
      // For now, we'll trigger alert if gap is exactly met (no excess)
      return gap.onSite === gap.required;
    });

    if (affectedRequirements.length > 0) {
      const alertMessage = affectedRequirements.length === 1
        ? `Checking out will create a gap for ${affectedRequirements[0].name}`
        : `Checking out will create gaps for ${affectedRequirements.length} requirements`;

      if (settings.show_toast_notifications) {
        toast.warning(alertMessage, {
          description: 'Compliance Alert',
          duration: 8000
        });
      }

      if (settings.show_browser_notifications) {
        showBrowserNotification('Checkout Warning', alertMessage);
      }
    }
  }, [settings, showBrowserNotification]);

  /**
   * Check for expiring certifications on-site
   * Checks against all configured expiry thresholds
   */
  const checkExpiringCertifications = useCallback((teamMembers, projectId) => {
    if (!settings?.alert_on_cert_expiring_onsite) return;
    if (expiryThresholds.length === 0) return;

    const now = new Date();
    const alerts = [];

    // Check each team member for expiring certifications
    teamMembers.forEach(member => {
      if (!member.certifications || member.certifications.length === 0) return;

      member.certifications.forEach(cert => {
        if (!cert.expiry_date) return;

        const expiryDate = new Date(cert.expiry_date);
        const daysUntilExpiry = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));

        // Check if this matches any of the user's configured thresholds
        expiryThresholds.forEach(threshold => {
          if (daysUntilExpiry === threshold.days_before_expiry) {
            alerts.push({
              type: 'cert_expiring',
              employeeName: member.name || 'Unknown',
              certName: cert.name || cert.qualification_name || 'Unknown Certification',
              daysUntilExpiry: threshold.days_before_expiry,
              severity: threshold.days_before_expiry <= 7 ? 'critical' : 'warning'
            });
          }
        });
      });
    });

    // Trigger alerts
    alerts.forEach(alert => {
      const title = 'Certificate Expiring On-Site';
      const message = `${alert.employeeName}'s ${alert.certName} expires in ${alert.daysUntilExpiry} day${alert.daysUntilExpiry !== 1 ? 's' : ''}`;

      if (settings.show_toast_notifications) {
        const toastFn = alert.severity === 'critical' ? toast.error : toast.warning;
        toastFn(message, {
          description: title,
          duration: 8000
        });
      }

      if (settings.show_browser_notifications) {
        showBrowserNotification(title, message);
      }

      if (alert.severity === 'critical') {
        playAlertSound();
      }
    });
  }, [settings, expiryThresholds, showBrowserNotification, playAlertSound]);

  return {
    settings,
    expiryThresholds,
    loading,
    analyzeGapChanges,
    checkCheckoutImpact,
    checkExpiringCertifications,
    requestNotificationPermission,
    refreshSettings: loadAlertSettings
  };
};
